import React from 'react';
import { Button as ReactstrapButton } from 'reactstrap';
import PropTypes from 'prop-types';

/**
 * Button component with design system variants
 *
 * Wraps reactstrap Button with semantic variants and consistent styling.
 * Accepts both `variant` (design system) and `color` (reactstrap compat) props.
 *
 * @example
 * <Button variant="primary">Click me</Button>
 * <Button color="primary">Click me</Button>  // reactstrap compat
 * <Button variant="success" leftIcon={<SaveIcon />}>Save</Button>
 * <Button variant="danger" loading>Deleting...</Button>
 */
const Button = ({
  variant,
  color,
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  className = '',
  ...props
}) => {
  // Support both `variant` (new) and `color` (reactstrap compat) props
  const resolvedVariant = variant || color || 'primary';

  // Map our variants to reactstrap color prop
  const variantMap = {
    primary: 'primary',
    secondary: 'secondary',
    success: 'success',
    danger: 'danger',
    outline: 'outline-primary',
    ghost: 'ghost',
  };

  // Map our sizes to reactstrap size prop
  const sizeMap = {
    sm: 'sm',
    md: undefined, // default
    lg: 'lg',
  };

  // Build class names
  const classNames = [
    className,
    fullWidth ? 'btn-full-width' : '',
    loading ? 'btn-loading' : '',
    leftIcon && !rightIcon ? 'btn-has-left-icon' : '',
    rightIcon && !leftIcon ? 'btn-has-right-icon' : '',
    leftIcon && rightIcon ? 'btn-has-left-icon btn-has-right-icon' : '',
    !children && (leftIcon || rightIcon) ? 'btn-icon' : '',
  ].filter(Boolean).join(' ');

  // Determine if this is a ghost/outline-secondary button
  const mappedColor = resolvedVariant === 'ghost' ? 'secondary' : variantMap[resolvedVariant];

  // For outline and ghost, we need outline prop
  const isOutline = resolvedVariant === 'outline' || resolvedVariant === 'ghost';

  return (
    <ReactstrapButton
      color={isOutline ? 'secondary' : mappedColor}
      outline={isOutline}
      size={sizeMap[size]}
      disabled={disabled || loading}
      className={classNames}
      {...props}
    >
      {loading && <span className="btn-spinner" />}
      {leftIcon}
      {children}
      {rightIcon}
    </ReactstrapButton>
  );
};

Button.propTypes = {
  /** Button variant (design system) */
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'outline', 'ghost']),
  /** Button color (reactstrap compat, alias for variant) */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Loading state */
  loading: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Button content */
  children: PropTypes.node,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional class names */
  className: PropTypes.string,
};

export default Button;
